import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { computeAge, getProfile, guessGender, saveProfile } from "@/lib/profile";
import { FEELINGS, feelingLabel } from "@/lib/feelings";
import PasswordInput from "@/components/julie/PasswordInput";
import Legal from "@/pages/Legal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function Connexion() {
  const navigate = useNavigate();
  const profile = getProfile();
  const [searchParams] = useSearchParams();
  const [selection, setSelection] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // mode "direct" (via « J'ai déjà mon espace ») : identifiant + mot de passe ensemble
  // mode "register" (parcours découverte) : PAGE UNIQUE — tout le formulaire d'un coup,
  // sans pré-étape e-mail (demande Julie : une page, une seule validation)
  const initialMode = searchParams.get("mode");
  const [mode, setMode] = useState(initialMode === "login" ? "direct" : initialMode === "register" ? "register" : "email");
  const directRegister = initialMode === "register";
  const [sending, setSending] = useState(false);
  // Messages d'erreur explicites, champ par champ (demande Julie)
  const [errors, setErrors] = useState({});
  const inputCls = (k) => `rav-input${errors[k] ? " is-error" : ""}`;
  const fieldErr = (k) =>
    errors[k] ? (
      <p className="rav-field-error" data-testid={`connexion-error-${k}`} role="alert">
        {errors[k]}
      </p>
    ) : null;
  const [cguAccepted, setCguAccepted] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null); // « cgu » | « ml » — fenêtre de lecture par-dessus le formulaire
  // Création de compte regroupée : prénom, naissance et choix de formule sur la même page
  const [prenom, setPrenom] = useState(profile?.prenom || "");
  const [birthdate, setBirthdate] = useState(profile?.birthdate || "");
  const [partnerCode, setPartnerCode] = useState(""); // code partenaire facultatif (ex. MARIE-RAV)

  // Une frappe dans un champ efface les erreurs affichées
  useEffect(() => {
    setErrors({});
  }, [prenom, birthdate, email, password, cguAccepted]);

  useEffect(() => {
    if (localStorage.getItem("rav-token")) {
      navigate("/accueil", { replace: true });
      return;
    }
    try {
      setSelection(JSON.parse(localStorage.getItem("rav-instant") || "null"));
    } catch {
      /* ignore */
    }
  }, [navigate]);

  const feeling = selection?.need ? FEELINGS.find((f) => f.f === selection.need) : null;
  const recapLabel = feeling ? feelingLabel(feeling, profile?.gender) : null;

  const continueEmail = async () => {
    if (!email.includes("@")) {
      toast("Écrivez votre adresse e-mail.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/auth/check-email`, { email });
      setMode(data.exists ? "login" : "register");
    } catch {
      toast("Une erreur est survenue. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  const submit = async () => {
    // Erreurs explicites champ par champ, en rouge sous le champ concerné (demande Julie)
    const errs = {};
    if (mode === "register") {
      if (prenom.trim().length < 2) errs.prenom = "Écrivez votre prénom.";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) errs.birthdate = "Indiquez votre date de naissance.";
    }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) errs.email = "Cette adresse e-mail ne semble pas valide.";
    if (password.length < 8) errs.password = "Votre mot de passe doit contenir au moins 8 caractères.";
    if (mode === "register" && !cguAccepted) errs.cgu = "Merci d'accepter les conditions générales d'utilisation pour créer votre espace.";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSending(true);
    try {
      let payload;
      if (mode === "register") {
        payload = { email, password, name: prenom.trim(), birthdate, gender: profile?.gender || null, age: computeAge(birthdate), cgu_accepted: cguAccepted };
        // Module Partenaires & Apporteurs : rattache l'inscription au partenaire d'origine
        // (dernier lien cliqué mémorisé localement, ou code partenaire saisi ci-dessus)
        try {
          const attr = JSON.parse(localStorage.getItem("rav-partner") || "null");
          if (attr?.link_id) {
            payload.partner_link_id = attr.link_id;
            payload.partner_ts = attr.ts || null;
          }
        } catch {
          /* ignore */
        }
        if (partnerCode.trim()) payload.partner_code = partnerCode.trim();
      } else {
        payload = { email, password };
      }
      const { data } = await axios.post(`${API}/auth/${mode === "register" ? "register" : "login"}`, payload);
      localStorage.setItem("rav-token", data.access_token);
      if (mode === "register") localStorage.removeItem("rav-partner");
      if (mode === "register") {
        saveProfile({ prenom: prenom.trim(), birthdate, age: computeAge(birthdate), gender: profile?.gender || "fille" });
      } else if (!getProfile() && data.access_token) {
        // Compte existant sur un nouvel appareil : on récupère le profil connu du serveur
        try {
          const { data: me } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${data.access_token}` } });
          if (me.user?.name) {
            saveProfile({
              prenom: me.user.name,
              birthdate: me.user.birthdate || null,
              age: me.user.age ?? computeAge(me.user.birthdate),
              gender: me.user.gender || guessGender(me.user.name) || "fille",
            });
          }
        } catch {
          /* ignore */
        }
      }
      toast(mode === "register" ? `Bienvenue ${prenom.trim()} ! Votre semaine de découverte est offerte — 7 jours, 7 Instants, sans carte.` : "Heureuse de vous revoir !");
      // Règle : une connexion (identifiant + mot de passe) arrive TOUJOURS sur l'accueil.
      // Exceptions : superadmin → /admin ; partenaire → son espace ;
      // création de compte → semaine de découverte SANS carte (Instant en attente ou accueil).
      if (data.user?.role === "superadmin") navigate("/admin");
      else if (data.user?.role === "partner") navigate("/partenaire/espace");
      else if (mode === "register") {
        let pendingReg = null;
        try {
          pendingReg = JSON.parse(localStorage.getItem("rav-instant") || "null");
        } catch {
          /* ignore */
        }
        navigate(pendingReg?.minutes ? "/instant" : "/accueil");
      } else {
        let pending = null;
        try {
          pending = JSON.parse(localStorage.getItem("rav-instant") || "null");
        } catch {
          /* ignore */
        }
        navigate(directRegister && pending?.minutes ? "/instant" : "/accueil");
      }
    } catch (err) {
      // E-mail déjà associé à un espace : bascule en douceur vers la connexion, sans tout refaire
      if (mode === "register" && err.response?.status === 409) {
        toast("Vous avez déjà un espace avec cette adresse — entrez votre mot de passe pour le retrouver.");
        setMode("login");
        setPassword("");
      } else {
        toast(err.response?.data?.detail || "Ces identifiants ne semblent pas corrects.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="rav-page" data-testid="connexion-page">
      <motion.img
        src="/logo-accueil.webp"
        alt="Respire Aime Vibre"
        className="rav-page-logo"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
      />
      <motion.div className="rav-connexion-body" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: EASE }}>
        <h1 className="rav-page-title rav-connexion-title" data-testid="connexion-title">
          {profile?.prenom ? `${profile.prenom}, votre` : "Votre"} <span className="rav-instant-em">INSTANT</span> vous attend
        </h1>
        {mode === "direct" && (
          <p className="rav-page-sub" data-testid="connexion-mode-hint">Entrez votre identifiant et votre mot de passe.</p>
        )}
        {mode !== "direct" && (
          <p className="rav-page-sub">Un dernier pas : créez votre espace pour le recevoir.</p>
        )}

        {mode !== "direct" && (
          <div className="rav-limit-card" data-testid="connexion-recap">
            <Heart size={24} strokeWidth={1.4} color="#B88652" />
            <p className="rav-limit-text" data-testid="connexion-recap-text">
              {recapLabel && selection?.minutes
                ? `${recapLabel} · ${selection.minutes} minutes`
                : "Votre sélection est prête"}
            </p>
          </div>
        )}

        {mode === "register" && (
          <>
            {/* Bandeau essai — grand et immédiatement lisible */}
            <div className="rav-limit-card" data-testid="connexion-trial-banner" style={{ textAlign: "center" }}>
              <p style={{ fontWeight: 700, fontSize: "1.15rem", margin: 0 }}>1 SEMAINE OFFERTE — SANS CARTE BANCAIRE</p>
              <p className="rav-page-sub" style={{ margin: "4px 0 0" }}>7 Instants à créer librement pendant 7 jours</p>
            </div>
            <label className="rav-field rav-welcome-fields">
              <span>Votre prénom</span>
              <input
                className={inputCls("prenom")}
                data-testid="connexion-prenom"
                type="text"
                autoComplete="given-name"
                placeholder="Écrivez votre prénom"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
              />
              {fieldErr("prenom")}
            </label>
            <label className="rav-field rav-welcome-fields">
              <span>Votre date de naissance</span>
              <input
                className={inputCls("birthdate")}
                data-testid="connexion-birthdate"
                type="date"
                min="1900-01-01"
                max={new Date().toISOString().slice(0, 10)}
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
              />
              {fieldErr("birthdate")}
            </label>
            {/* Semaine de découverte — SANS carte bancaire (nouveau parcours validé par Julie) */}
            <div className="rav-field" data-testid="connexion-plan-choice">
              <p className="rav-page-sub" data-testid="connexion-plan-terms" style={{ marginTop: 0 }}>
                <strong>Votre semaine de découverte :</strong> 7 jours offerts et 7 Instants à créer librement —
                <strong> sans carte bancaire</strong>. Ensuite, seulement si RAV vous fait du bien, vous choisirez
                votre formule : mensuelle (9,90 € TTC/mois) ou annuelle (89 € TTC/an), sans engagement.
                Sans choix de votre part, rien ne sera jamais prélevé.
              </p>
            </div>
          </>
        )}

        <label className="rav-field rav-welcome-fields">
          <span>Votre adresse e-mail</span>
          <input
            className={inputCls("email")}
            data-testid="connexion-email"
            type="email"
            autoComplete="email"
            placeholder="ex. julie@email.fr"
            value={email}
            disabled={mode === "login" || (mode === "register" && !directRegister)}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
          />
          {fieldErr("email")}
        </label>

        {mode === "email" ? (
          <button
            type="button"
            className="rav-primary-button"
            data-testid="connexion-continue"
            disabled={sending}
            onClick={continueEmail}
          >
            Continuer
          </button>
        ) : (
          <>
            {mode !== "direct" && (
              <p className="rav-page-sub" data-testid="connexion-mode-hint">
                {mode === "login"
                  ? "Vous avez déjà un espace — entrez votre mot de passe."
                  : "Choisissez un mot de passe pour sécuriser votre espace."}
              </p>
            )}
            <label className="rav-field rav-welcome-fields">
              <span>Votre mot de passe</span>
              <PasswordInput
                testId="connexion-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                placeholder="8 caractères minimum"
              />
              {fieldErr("password")}
            </label>
            {mode === "register" && (
              <label className="rav-field rav-welcome-fields">
                <span>Un code partenaire ? (facultatif)</span>
                <input
                  className={inputCls("partner")}
                  data-testid="connexion-partner-code"
                  type="text"
                  autoComplete="off"
                  placeholder="ex. MARIE-RAV"
                  value={partnerCode}
                  onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
                />
              </label>
            )}
            {mode === "register" && (
              <label className="rav-cgu-row" data-testid="connexion-cgu-row">
                <input
                  type="checkbox"
                  data-testid="connexion-cgu-checkbox"
                  checked={cguAccepted}
                  onChange={(e) => setCguAccepted(e.target.checked)}
                />
                <span>
                  J'ai lu et j'accepte les{" "}
                  <button type="button" className="rav-cgu-link" data-testid="connexion-cgu-link" onClick={(e) => { e.preventDefault(); setLegalDoc("cgu"); }}>
                    conditions générales d'utilisation
                  </button>{" "}
                  et les{" "}
                  <button type="button" className="rav-cgu-link" data-testid="connexion-ml-link" onClick={(e) => { e.preventDefault(); setLegalDoc("ml"); }}>
                    mentions légales
                  </button>
                </span>
              </label>
            )}
            {mode === "register" && fieldErr("cgu")}
            <button
              type="button"
              className="rav-primary-button"
              data-testid="connexion-submit"
              disabled={sending}
              onClick={submit}
            >
              {mode === "register" ? "Créer mon espace" : "Me connecter"}
            </button>
            {mode === "direct" ? (
              <button
                type="button"
                className="rav-text-link rav-link-strong"
                data-testid="connexion-to-register"
                onClick={() => navigate("/bienvenue")}
              >
                Pas encore d'espace ? Créer mon compte
              </button>
            ) : directRegister && mode === "register" ? (
              <button
                type="button"
                className="rav-text-link"
                data-testid="connexion-to-login"
                onClick={() => navigate("/connexion?mode=login")}
              >
                Déjà un espace ? Me connecter
              </button>
            ) : (
              <button
                type="button"
                className="rav-text-link"
                data-testid="connexion-change-email"
                onClick={() => {
                  setMode("email");
                  setPassword("");
                }}
              >
                Modifier mon adresse e-mail
              </button>
            )}
          </>
        )}
      </motion.div>

      {/* CGU / mentions légales lues PAR-DESSUS le formulaire : rien n'est perdu,
          et « J'ai lu » ramène directement à la case à cocher */}
      {legalDoc && (
        <div className="rav-legal-overlay" data-testid="legal-overlay">
          <Legal doc={legalDoc} onClose={() => setLegalDoc(null)} />
        </div>
      )}
    </main>
  );
}
